// ─────────────────────────────────────────────────────────────────────────────
// LineChart.js — User Activity and Traffic Trend, the two green line charts.
//
// Mirrors the Dashboard's Activity/Bandwidth charts (smooth monotone curve,
// dashed grid, gradient area, hover crosshair + dot, hover card) via the shared
// chartMath, but in green. It measures the plot's real px width AND height so
// the viewBox is 1:1 with the rendered box — no preserveAspectRatio stretching,
// which keeps strokes even and the hover dot round.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef, useEffect } from "react";
import { useElementSize } from "../../../shared/hooks";
import { monotoneXPath, areaPath, niceCeiling, formatTick } from "../../../shared/lib/chartMath";
import { ChartBody, ChartPlaceholder } from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import styles from "./LineChart.module.css";

const LC = { left: 40, rightPad: 14, top: 16, bottomPad: 28 };

/**
 * @param {object} props
 * @param {{label: string, val: number}[] | null} props.data  null = still loading
 * @param {boolean} props.loading
 * @param {string}  props.label         card heading
 * @param {string}  props.tooltipLabel  caption above the hover value
 */
export default function LineChart({ data, loading, label, tooltipLabel }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [plotRef, { width: W, height: H }] = useElementSize({ width: 480, height: 220 });
  const svgRef = useRef();

  // Drop the hover whenever the series changes — a stale index would read past
  // the end of the new array and crash the tooltip.
  useEffect(() => { setHoverIdx(null); }, [data]);

  // null = still loading; length < 2 = arrived but not enough points to draw.
  if (!data || data.length < 2) {
    return <ChartPlaceholder label={label} loading={loading || data === null} />;
  }

  const right  = W - LC.rightPad;
  const bottom = H - LC.bottomPad;

  const vals = data.map((d) => d.val);
  const n = vals.length;
  const maxV = niceCeiling(Math.max(...vals, 0));

  const xs = vals.map((_, i) => LC.left + (n > 1 ? (i / (n - 1)) * (right - LC.left) : 0));
  const ys = vals.map((v) => bottom - (v / maxV) * (bottom - LC.top));

  const line = monotoneXPath(xs, ys);
  const area = areaPath(line, xs, bottom);
  const gradTop = Math.min(Math.min(...ys), bottom - 1);

  const yTicks = [0, 1, 2, 3, 4].map((i) => LC.top + (i / 4) * (bottom - LC.top));

  const handleMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((svgX - LC.left) / (right - LC.left)) * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
  };

  const active = hoverIdx !== null ? { x: xs[hoverIdx], y: ys[hoverIdx] } : null;
  const [hoverTitle, hoverSub] = hoverIdx !== null ? data[hoverIdx].label.split("\n") : [];

  // Flip the card near the edges so it never overflows the card bounds.
  const tooltipTransform = active
    ? (active.x < W * 0.2 ? "translate(0, -115%)"
      : active.x > W * 0.8 ? "translate(-100%, -115%)"
      : "translate(-50%, -115%)")
    : undefined;

  // Each instance needs its own gradient id — two <defs> sharing one id would
  // make whichever mounted first win for both charts.
  const gradId = `lc-grad-${label.replace(/[^a-z0-9]/gi, "")}`;

  return (
    <ChartBody label={label}>
      <div className={styles.plot} ref={plotRef}>
        <svg
          ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={styles.svg}
          onMouseMove={handleMove} onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id={gradId} x1={LC.left} y1={gradTop} x2={LC.left} y2={bottom} gradientUnits="userSpaceOnUse">
              <stop offset="5%"  stopColor="#00FFA3" stopOpacity="0.3" />
              <stop offset="95%" stopColor="#00FFA3" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid — dashed 3 3, white @ 4% (horizontal + vertical) */}
          {yTicks.map((y, i) => (
            <line key={`y-${i}`} x1={LC.left} y1={y} x2={right} y2={y} stroke="white" strokeOpacity="0.04" strokeDasharray="3 3" />
          ))}
          {xs.map((x, i) => (
            <line key={`x-${i}`} x1={x} y1={LC.top} x2={x} y2={bottom} stroke="white" strokeOpacity="0.04" strokeDasharray="3 3" />
          ))}

          {/* Y-axis tick labels */}
          {yTicks.map((y, i) => (
            <text key={`yt-${i}`} x={LC.left - 9} y={y + 4} textAnchor="end" fontSize="10"
              fill="rgba(255,255,255,0.3)" fontFamily="JetBrains Mono, monospace">
              {formatTick(maxV * (1 - i / 4))}
            </text>
          ))}

          {/* X-axis labels — an optional 2nd line comes through as "\n" */}
          {data.map((d, i) => {
            const [first, second] = d.label.split("\n");
            return (
              <text key={`xt-${i}`} x={xs[i]} y={bottom + 15} textAnchor="middle" fontSize="10"
                fill="rgba(255,255,255,0.3)" fontFamily="JetBrains Mono, monospace">
                <tspan x={xs[i]}>{first}</tspan>
                {second && <tspan x={xs[i]} dy="11">{second}</tspan>}
              </text>
            );
          })}

          {/* Area + dual-stroke monotone line (#10B981 w2 under #00FFA3 w1.5) */}
          {line && (
            <>
              <path d={area} fill={`url(#${gradId})`} fillOpacity="0.6" />
              <path d={line} fill="none" stroke="#10B981" strokeWidth="2"   strokeLinecap="round" strokeLinejoin="round" />
              <path d={line} fill="none" stroke="#00FFA3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}

          {/* Hover crosshair + marker (black dot, #00FFA3 ring) */}
          {active && (
            <>
              <line x1={active.x} y1={LC.top} x2={active.x} y2={bottom - 1} stroke="white" strokeOpacity="0.16" />
              <circle cx={active.x} cy={active.y} r="5" fill="black" stroke="#00FFA3" strokeWidth="4" />
            </>
          )}
        </svg>

        {active && (
          <ChartTooltip
            title={hoverTitle}
            sub={hoverSub}
            label={tooltipLabel}
            value={data[hoverIdx].val.toLocaleString()}
            style={{
              left: `${(active.x / W) * 100}%`,
              top:  `${(active.y / H) * 100}%`,
              transform: tooltipTransform,
            }}
          />
        )}
      </div>
    </ChartBody>
  );
}
